/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Switch, Route, BrowserRouter, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import EmailTooltipMAIQ from './components/EmailTooltipMAIQ';

import { GlobalStyle } from 'styles/global-styles';

import { LoginPage } from 'app/pages/LoginPage/Loadable';
import { DormantCampaignsViewPage } from 'app/pages/DormantCampaignsViewPage/Loadable';
import { DormantCampaignsLeadsPage } from 'app/pages/DormantCampaignsLeadsPage/Loadable';
import { DormantCampaignsLeadsDetailsPage } from 'app/pages/DormantCampaignsLeadsDetailsPage/Loadable';
import { TwoFactorLoginPage } from 'app/pages/TwoFactorLoginPage/Loadable';
import { DealerDashboardPage } from 'app/pages/AllDealershipGridPage/Loadable';
import { CrmMessageDetailsPage } from 'app/pages/CrmMessageDetailsPage/Loadable';
import { ShareableNurturingHistoryPage } from 'app/pages/ShareableNurturingHistoryPage/Loadable';
import { ForgotPasswordPage } from 'app/pages/ForgotPasswordPage/Loadable';
import { ResetPasswordPage } from 'app/pages/ResetPasswordPage/Loadable';
import { NotFoundPage } from 'app/components/NotFoundPage/Loadable';
import { HomePage } from 'app/pages/HomePage/Loadable';
// import { useTranslation } from 'react-i18next';
import {
  UnAuthorizedRoute,
  AuthorizedRoute,
  MyautoiqRoute,
  FlexibleRoute,
} from 'app/components/AuthRouter';
import { NotificationAlert } from 'app/pages/NotificationAlert/Loadable';
import IdleTimeoutHandler from 'app/components/IdleTimeoutHandler';
import AuthService from 'app/services/AuthService';
// import { DriveAuth } from './pages/Drive';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { isAuthenticated, getRoutes } from 'utils/auth';
import { refreshServerProfile } from 'utils/sessionBootstrap';
import { headerActions, reducer, sliceKey } from 'app/pages/Header/slice';
import { headerSaga } from 'app/pages/Header/saga';

import { routesMap } from 'app/components/AuthRouter/authRoutes';
import { selectRoutes } from 'app/pages/LoginPage/selectors';
import { AIAvatarRoute } from 'app/components/AuthRouter/AIAvatarRoute';
import { AIAvatarLayout } from 'app/NewAIAvatarLayout';
import { DealerLaunchProgressProvider } from 'app/pages/Header/context/DealerLaunchProgressContext';
import { GuidanceTourProvider } from 'app/components/ReusableUIComponents/HighLevelComponents/GuidanceTourMAIQ';

function AppRoutes() {
  const location = useLocation();
  const isPublicRoute =
    location.pathname === '/login' ||
    location.pathname === '/forgot-password' ||
    location.pathname.startsWith('/reset-password') ||
    location.pathname === '/2FA-login';

  const [sessionReady, setSessionReady] = useState(isPublicRoute);

  useEffect(() => {
    if (isPublicRoute) {
      setSessionReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await refreshServerProfile();
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPublicRoute]);

  const routesState = useSelector(selectRoutes);
  const [routes, setRoutes] = useState<any>(() => getRoutes());

  const userObj = AuthService.getUser();
  const idleTimeout = userObj?.session_idle_timeout ?? 60;
  const alertModalTime = userObj?.session_idle_modal_warning ?? 2;
  const accessTokenTimeout = userObj?.access_token_timeout ?? 600;

  useEffect(() => {
    if (routesState?.length) {
      setRoutes(routesState);
    } else {
      const localRoutes = getRoutes();
      if (localRoutes?.length) setRoutes(localRoutes);
    }
  }, [routesState]);

  useEffect(() => {
    if (!sessionReady || isPublicRoute) return;
    setRoutes(getRoutes());
  }, [sessionReady, isPublicRoute]);

  const user = isAuthenticated();

  if (!sessionReady) {
    return null;
  }

  return (
    <>
      <Helmet
        titleTemplate="%s - Myautoiq Admin"
        defaultTitle="Myautoiq Admin"
        htmlAttributes={
          {
            // lang: i18n.language
          }
        }
      >
        <meta name="description" content="A Myautoiq application" />
      </Helmet>
      <NotificationAlert />
      {user && (
        <IdleTimeoutHandler 
          onLogout={AuthService.logout}
          accessTokenTimeout={accessTokenTimeout}
          idleTimeout={idleTimeout}
          alertModalTime={alertModalTime}
        />
      )}
      <EmailTooltipMAIQ />
      <Switch>
        <UnAuthorizedRoute exact path="/login" component={LoginPage} />
        <UnAuthorizedRoute
          exact
          path="/forgot-password"
          component={ForgotPasswordPage}
        />
        <UnAuthorizedRoute
          path="/reset-password"
          component={ResetPasswordPage}
        />
        <UnAuthorizedRoute
          exact
          path="/2FA-login"
          component={TwoFactorLoginPage}
        />
        {/* <UnAuthorizedRoute
          exact
          path="/url/:short_url_id"
          component={CrmMessageDetailsPage}
        /> */}
        <UnAuthorizedRoute
          exact
          path="/nurtured_history"
          component={CrmMessageDetailsPage}
        />
        <FlexibleRoute
          exact
          path="/url/:shortUrlId"
          component={ShareableNurturingHistoryPage}
        />
        <UnAuthorizedRoute
          exact
          path="/2FA-login"
          component={TwoFactorLoginPage}
        />
        {/* render in myautoiq angular */}
        <MyautoiqRoute
          exact
          path="/myautoiq/dormant-campaigns-view/:dealerId"
          component={DormantCampaignsViewPage}
        />
        <MyautoiqRoute
          exact
          path="/myautoiq/dormant-campaigns-leads/:id"
          component={DormantCampaignsLeadsPage}
        />
        <MyautoiqRoute
          exact
          path="/myautoiq/dormant-leads-view/:dealerId/:mailshakeCampaignId/:mailshakeEmailAddress"
          component={DormantCampaignsLeadsDetailsPage}
        />
        <AuthorizedRoute exact path="/" component={HomePage} />

        {/* AI Avatar Layout Routes - Only for beta_maiq_performance_manager */}
        <AIAvatarRoute path="/ai-avatar-layout" component={AIAvatarLayout} />

        {/* Email templates: static routes so Create/Edit always work even if not in API resource_mappings */}
        {/* <AuthorizedRoute
          exact
          path="/emails-create"
          component={routesMap.EmailTemplatesCreateEditPage}
        />
        <AuthorizedRoute
          path="/emails-edit/:email_template_id"
          component={routesMap.EmailTemplatesCreateEditPage}
        /> */}

        <AuthorizedRoute
          exact
          path="/configure"
          component={routesMap.Configure}
        />
        <AuthorizedRoute
          exact
          path="/content"
          component={routesMap.Content}
        />
        <AuthorizedRoute
          exact
          path="/configure-calendar"
          component={routesMap.CalendarConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-segments"
          component={routesMap.SegmentsConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-template-value"
          component={routesMap.TemplateValueConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-template-message-type"
          component={routesMap.SegmentMessageConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-static-content"
          component={routesMap.StaticContentConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-nurturing-journey"
          component={routesMap.NurturingJourneySettingsPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-cdp-settings"
          component={routesMap.CdpSettingsConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/nurturing-plan-generator"
          component={routesMap.NurturingPlanGeneratorPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-launch-settings"
          component={routesMap.LaunchSettingsConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/configure-inventory-feed-settings"
          component={routesMap.InventoryFeedSettingsConfigurationPage}
        />
        <AuthorizedRoute
          exact
          path="/monitor"
          component={routesMap.Monitor}
        />
        <AuthorizedRoute
          exact
          path="/monitor-nurturing-candidate-audit"
          component={routesMap.NurturingCandidateAuditPage}
        />
        <AuthorizedRoute
          exact
          path="/monitor-nurturing-plan-execution"
          component={routesMap.NurturingPlanExecutionPage}
        />
        <AuthorizedRoute
          exact
          path="/human-in-the-loop"
          component={routesMap.HumanInTheLoopPage}
        />

        {(Array.isArray(routes) ? routes : []).map((route, index) => {
          const RouteComponent = route?.path ? (routesMap[route.component] ?? NotFoundPage) : null;
          return RouteComponent ? (
            <AuthorizedRoute
              key={route.path ?? index}
              exact
              path={route.path}
              component={RouteComponent}
              roles={route?.roles || null}
            />
          ) : null;
        })}
        <AuthorizedRoute component={NotFoundPage} />
      </Switch>
      <GlobalStyle />
    </>
  );
}

export function App() {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  return (
    <BrowserRouter>
      <GuidanceTourProvider>
        <DealerLaunchProgressProvider>
          <AppRoutes />
        </DealerLaunchProgressProvider>
      </GuidanceTourProvider>
    </BrowserRouter>
  );
}
